/** @jsx jsx */

import type { InputHTMLAttributes } from 'react';
import { forwardRef, useCallback, useState } from 'react';
import { jsx } from '@balance-web/core';
import { buildDataAttributes } from '@balance-web/core';
import type { WithDataAttributeProp } from '@balance-web/core';
import { useFieldContext } from '@balance-web/field';
import { useTheme } from '@balance-web/theme';
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from '@balance-web/icon';
import { IconButton } from '@balance-web/button';

import { useInputStyles } from './utils';
import type { InputSizeType } from './types';
import { Adornment, AdornmentWrapper } from './Adornments';

const validTypes = {
  email: 'email',
  number: 'number',
  password: 'password',
  search: 'search',
  tel: 'tel',
  text: 'text',
  url: 'url',
};

type InputProps = InputHTMLAttributes<HTMLInputElement>;
export type TextInputProps = WithDataAttributeProp<
  {
    // FIXME: temp solution for invalid input w/o displaying an invalid message. we might need a "hideMessage" prop on `Field` to support assistive tech
    invalid?: boolean;
    onChange: NonNullable<InputProps['onChange']>;
    size?: InputSizeType;
    type?: keyof typeof validTypes;
    value: NonNullable<InputProps['value']>;
    /**
     * Hide the invalid adornment in cases where TextInput is overlaid as a toggle on top of another
     * input with an adornment such as DatePicker.
     */
    hideInvalidAdornment?: boolean;
  } & Omit<InputProps, 'onChange' | 'type' | 'size' | 'value'>
>;

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      invalid: invalidProp,
      size = 'medium',
      type = 'text',
      hideInvalidAdornment,
      data,
      ...props
    },
    ref
  ) => {
    const { typography } = useTheme();
    const styles = useInputStyles({ size, shape: 'square' });
    const { invalid, ...a11yProps } = useFieldContext();
    const isInvalid = invalidProp ?? invalid;
    const [passwordType, setPasswordType] = useState<'password' | 'text'>(
      'password'
    );

    const dataAttributes = buildDataAttributes(data);

    const handleClick = useCallback(() => {
      setPasswordType((currentType) => {
        return currentType === 'password' ? 'text' : 'password';
      });
    }, [setPasswordType]);

    return (
      <AdornmentWrapper size={size} shape="round">
        <input
          aria-invalid={isInvalid}
          ref={ref}
          type={type === 'password' ? passwordType : type}
          css={styles}
          {...a11yProps}
          {...props}
          {...dataAttributes}
        />
        {type === 'password' && (
          <Adornment align="right">
            <IconButton
              icon={passwordType === 'text' ? EyeIcon : EyeOffIcon}
              colorScheme="tertiary"
              variant="text"
              label={
                passwordType === 'text' ? 'Hide password' : 'Show password'
              }
              size={size}
              onClick={handleClick}
            />
          </Adornment>
        )}
        {!hideInvalidAdornment && isInvalid && type !== 'password' && (
          <Adornment
            align="right"
            css={{
              fontSize: typography.fontSize[size],
              pointerEvents: 'none',
            }}
          >
            <AlertCircleIcon size={size} color="critical" />
          </Adornment>
        )}
      </AdornmentWrapper>
    );
  }
);
